/* SharePanel (Phase 114): share a PUBLIC report. Uses the permanent share
   link (/api/share/report/{tn}) which serves Open Graph tags to crawlers and
   forwards people to the public page. Email sharing through the website
   attaches approved public photos only and sends only when the user presses
   Send; the plain "Email app" option is a mailto link with text only. */
import React, { useEffect, useState } from 'react';
import { Check, Copy, Facebook, Link2, Loader2, Mail, Send, Share2, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { api } from '../lib/api';

export const shareLinkFor = (trackingNumber) => `${window.location.origin}/api/share/report/${trackingNumber}`;

export const SharePanel = ({ report }) => {
  const tn = report.tracking_number;
  const link = shareLinkFor(tn);
  const title = `${report.title || 'Ward 8 issue report'} (${tn})`;
  const summary = (report.public_summary || '').slice(0, 220);
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ recipients: '', sender_name: '', message: '', include_photos: true, website: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const photoCount = (report.photos || []).length;

  useEffect(() => {
    api.get(`/public/reports/${encodeURIComponent(tn)}/share-email/status`).then((r) => setStatus(r.data)).catch(() => setStatus({ available: false }));
  }, [tn]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success('Public report link copied'); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Could not copy. Long-press the link to copy it.'); }
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title, text: summary, url: link }); } catch { /* user cancelled */ } } else { copy(); }
  };
  const openWin = (u) => window.open(u, '_blank', 'noopener,noreferrer,width=640,height=560');
  const mailto = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${summary ? summary + '\n\n' : ''}Public report ${tn} (status: ${report.status_label || report.status}).\n${link}`)}`;

  const sendEmail = async (e) => {
    e.preventDefault();
    const recipients = form.recipients.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!recipients.length) { toast.error('Enter at least one recipient email address'); return; }
    if (recipients.length > (status?.max_recipients || 5)) { toast.error(`Up to ${status?.max_recipients || 5} recipients per email`); return; }
    setSending(true);
    try {
      const r = await api.post(`/public/reports/${encodeURIComponent(tn)}/share-email`, { ...form, recipients });
      setSent(r.data?.message || 'Sent.');
      toast.success('Share email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="share-panel">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => openWin(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`)} className="h-11 font-bold btn-press" data-testid="share-facebook"><Facebook className="h-4 w-4 mr-1.5" /> Facebook</Button>
        <Button type="button" variant="outline" onClick={() => openWin(`https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent(title)}`)} className="h-11 font-bold btn-press" data-testid="share-x"><Twitter className="h-4 w-4 mr-1.5" /> X</Button>
        <Button type="button" variant="outline" onClick={copy} className="h-11 font-bold btn-press" data-testid="share-copy">{copied ? <Check className="h-4 w-4 mr-1.5 text-emerald-700" /> : <Copy className="h-4 w-4 mr-1.5" />} Copy link</Button>
        <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="share-mailto"><a href={mailto}><Mail className="h-4 w-4 mr-1.5" /> Email app (text only)</a></Button>
        <Button type="button" onClick={() => setEmailOpen((v) => !v)} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="share-email-website"><Send className="h-4 w-4 mr-1.5" /> Email from website{photoCount ? ' with photos' : ''}</Button>
        {typeof navigator !== 'undefined' && navigator.share && (
          <Button type="button" variant="outline" onClick={nativeShare} className="h-11 font-bold btn-press" data-testid="share-native"><Share2 className="h-4 w-4 mr-1.5" /> More</Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2 inline-flex items-start gap-1.5" data-testid="share-link-text"><Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span className="break-all">{link}</span></p>
      <p className="text-xs text-slate-500 mt-1.5" data-testid="share-preview-note">
        Social platforms read the report title, a short excerpt and {photoCount > 1 ? 'a collage of the approved public photos' : photoCount === 1 ? 'the approved public photo' : 'a branded card'} from this link. The platform controls the final preview and may not display every photo. The "Email app" option sends text and the link only; it cannot attach photos.
      </p>

      {emailOpen && (
        <form onSubmit={sendEmail} className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="share-email-form">
          <p className="font-serif font-bold text-[#1a2744]">Email this public report from the website</p>
          {status && !status.available && (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="share-email-unavailable">Email sharing from the website is not switched on yet. Use Copy link or the Email app option for now.</p>
          )}
          {sent ? (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="share-email-sent">{sent}</p>
          ) : (
            <>
              <div>
                <Label htmlFor="share-recipients">Recipient email addresses <span className="text-slate-400 font-normal">(up to {status?.max_recipients || 5}, separated by commas)</span></Label>
                <Input id="share-recipients" value={form.recipients} onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))} placeholder="neighbour@example.com, councillor@example.com" className="mt-1 h-11" required data-testid="share-email-recipients" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="share-sender">Your name <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input id="share-sender" value={form.sender_name} onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))} maxLength={80} className="mt-1 h-11" data-testid="share-email-sender" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-700 h-11 cursor-pointer">
                    <Checkbox checked={form.include_photos} onCheckedChange={(v) => setForm((f) => ({ ...f, include_photos: !!v }))} disabled={!photoCount} data-testid="share-email-include-photos" />
                    Attach the {photoCount} approved public photo{photoCount === 1 ? '' : 's'}
                  </label>
                </div>
              </div>
              <div>
                <Label htmlFor="share-message">Short message <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea id="share-message" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} maxLength={600} rows={3} className="mt-1" data-testid="share-email-message" />
              </div>
              <div className="hidden" aria-hidden="true"><input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} /></div>
              <p className="text-xs text-slate-500">The email contains the public title, summary, status, tracking number and this link, plus approved public photos if ticked. No private details. Nothing is sent until you press Send.</p>
              <Button type="submit" disabled={sending || (status && !status.available)} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="share-email-send">{sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send</Button>
            </>
          )}
        </form>
      )}
    </div>
  );
};
